import { forwardRef, type ButtonHTMLAttributes, type JSX } from 'react';
import clsx from 'clsx';

type PillButtonVariant = 'primary' | 'secondary';
type PillButtonSize = 'sm' | 'md';

interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PillButtonVariant;
  size?: PillButtonSize;
}

const baseClasses =
  'inline-flex min-h-11 items-center justify-center rounded-full font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:cursor-not-allowed disabled:opacity-60 md:min-h-0';

const variantClasses: Record<PillButtonVariant, string> = {
  primary: 'bg-brand-accent text-white shadow-sm hover:bg-brand-accent/90',
  secondary:
    'border border-brand-accent/60 text-brand-accent hover:border-brand-accent hover:bg-brand-accent/10',
};

const sizeClasses: Record<PillButtonSize, string> = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-2 text-xs uppercase tracking-wide',
};

/**
 * PillButton centralizes the rounded pill CTA styling shared across dashboard surfaces.
 */
export const PillButton = forwardRef<HTMLButtonElement, PillButtonProps>(function PillButton(
  { variant = 'primary', size = 'md', className, type = 'button', ...rest },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      {...rest}
    />
  );
});

PillButton.displayName = 'PillButton';
